// TODO update this to import from commons/src/gql
// eslint-disable-next-line no-restricted-imports
import { gql } from '@apollo/client';
import React, { useState } from 'react';
import I18n from 'i18n-js';
import classNames from 'classnames';
import { isEmpty } from 'lodash';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { formatInternationalizedSeconds } from '../date_utils';
import { isOwner } from '../user_context_helpers';
import { isItemPage, listingItemPagePath, CART_PATH } from '@reverbdotcom/commons/src/url_helpers';
import { MParticleEvent } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { WEB } from '@reverbdotcom/commons/src/constants';
import { useMutation } from '@reverbdotcom/commons/src/useMutation';
import { flashError } from '@reverbdotcom/commons/src/flash';

import {
  AddToCartButtonFields,
  AddToCartInput,
  BumpKeyFragment,
  core_apimessages_Listing_ListingType as ListingType,
  core_apimessages_ProtectionPlan,
} from '../gql/graphql';

import {
  MParticleEventName,
  ProductActionType,
  trackEvent,
} from '../elog/mparticle_tracker';
import { trackCriteoAddToCart } from '../criteo';
import { trackFacebookEvent, ADD_TO_CART_EVENT, PRODUCT_CONTENT_TYPE } from '@reverbdotcom/commons/src/facebook';
import { withRouter, WithRouterProps } from 'react-router';
import { Location } from 'history';
import { resolveCurrentView } from '../event_tracking/favorites';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { RCLoadingBars } from '@reverbdotcom/cadence/components';
import ProtectionPlanModal from '@reverbdotcom/commons/src/components/ProtectionPlanModal';
import { trackProtectionPlanEvent, formattedProtectionPlans } from '@reverbdotcom/commons/src/components/protectionPlanHelpers';
import window from '@reverbdotcom/commons/src/window_wrapper';

/**
 * Bump tracking is conditionally provided by the parent component
 * separately from the required listing fragment.
 *
 * Ideally this would be controlled by a separate flag prop but has
 * widespread enough use that the existing behavior should be kept.
 */
type Listing = AddToCartButtonFields.Fragment & BumpKeyFragment;

interface ExternalProps {
  listing: Listing;
  quantity?: number;
  disabled?: boolean;
  listingClickEvent?: MParticleEvent;
  position?: number;
  parentComponentName?: string;
  redirectPath?: string;
  protectionPlanId?: string;
  availableProtectionPlans?: core_apimessages_ProtectionPlan[];
  setProtectionPlanId?: (protectionPlanId: string) => void;
  suggestedListing?: boolean;
}

type Props = ExternalProps & WithRouterProps;

const ADD_TO_CART = gql(`
  mutation Core_AddToCart($input: AddToCartInput!) {
    addToCart(input: $input) {
      listingId
    }
  }
`);

export function AddToCartButton({
  listing,
  quantity = 1,
  disabled = false,
  position = null,
  parentComponentName = null,
  redirectPath = null,
  location,
  listingClickEvent = null,
  protectionPlanId = null,
  availableProtectionPlans = [],
  setProtectionPlanId = null,
  suggestedListing = false,
}: Props) {
  const view = useViewModel({
    listing,
    quantity,
    disabled,
    position,
    parentComponentName,
    location,
  });
  const [isProtectionPlanModalOpen, setProtectionPlanModalOpen] = useState(false);
  const [ defaultFetchedProtectionPlan ] = availableProtectionPlans;
  const selectedProtectionPlan = availableProtectionPlans?.find(pp => pp.planId === protectionPlanId);
  const displayedProtectionPlan = selectedProtectionPlan || defaultFetchedProtectionPlan;
  const additionalCoverageDetails = defaultFetchedProtectionPlan?.additionalPlanCoverageDetails || [];

  const isBulkItem = quantity > 1;
  const renderProtectionPlanModal = isItemPage(location) && !protectionPlanId && !isEmpty(availableProtectionPlans) && !isBulkItem;

  const [ addToCartMutation ] = useMutation(ADD_TO_CART);
  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);

  const addToCartInput = {
    actionSourceAttributes: { device: 'web' },
    bumpKey: (listing?.bumpKey?.key ? { key: listing.bumpKey.key } : null),
    listingId: view.listingId,
    protectionPlanId: protectionPlanId,
    quantity: quantity || 1,
    referer: window?.document?.referrer,
    suggestedListing: suggestedListing,
  };

  function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    view.handleClick();

    if (renderProtectionPlanModal) {
      setProtectionPlanModalOpen(true);
      trackProtectionPlanExp();
    } else {
      submitAddToCart(addToCartInput);
    }
  }

  function trackProtectionPlanExp() {
    if (!protectionPlanId && !isEmpty(availableProtectionPlans)) {
      trackEvent({
        eventName: MParticleEventName.ProtectionPlanQualifyingEvent,
        listingId: listing.id,
        componentName: parentComponentName,
        protectionPlans: formattedProtectionPlans(availableProtectionPlans),
      });
    }
  }

  async function submitAddToCart(addToCartData: AddToCartInput) {
    setShowLoadingSpinner(true);
    const response = await addToCartMutation({
      variables: {
        input: addToCartData,
      },
    });

    if (response.errors?.length > 0) {
      setShowLoadingSpinner(false);
      flashError(response.errors.map((e) => e.message).join(' '));
      return;
    }

    // Set _atct (Add To Cart Tracking) query param with product info for ad scripts
    const redirectUrl = new URL(redirectPath || CART_PATH, window.location.origin);
    redirectUrl.searchParams.set(
      '_atct',
      btoa(JSON.stringify({
        id: listing.id,
        price: listing.pricing.buyerPrice.amount,
        currency: listing.pricing.buyerPrice.currency,
      })),
    );

    window.location.assign(redirectUrl);
  }

  function onProtectionPlanModalSubmit(selectedPlan: core_apimessages_ProtectionPlan) {
    setProtectionPlanId(selectedPlan.planId);
    submitAddToCart({ ...addToCartInput, protectionPlanId: selectedPlan.planId });
  }

  function onProtectionPlanModalClose() {
    submitAddToCart(addToCartInput);
  }

  if (view.showDigitalListingLink) {
    return (
      <CoreLink
        className="add-to-cart-button"
        to={listingItemPagePath(listing)}
        target={view.freeListingAnchorTarget}
        clickEvent={listingClickEvent}
      >
        {I18n.t('commons.gridCardRow.viewListing')}
      </CoreLink>
    );
  }

  if (view.showDisabled) {
    return (
      <>
        <div
          className="add-to-cart-button"
          data-disabled
        >
          {view.title}
        </div>
        {view.showPreorderText &&
          view.preorderDateText
        }
      </>
    );
  }

  return (
    <div>
      <button
        className="add-to-cart-button"
        onClick={handleClick}
        disabled={showLoadingSpinner}
      >
        <div className={classNames('add-to-cart-button__content', { loading: showLoadingSpinner })}>
          <span className="add-to-cart-button__spinner"><RCLoadingBars size="small" /></span>
          <span className="add-to-cart-button__text">{view.title}</span>
        </div>
      </button>

      {view.showPreorderText &&
        <div className="add-to-cart-button__preorder-date">
          {view.preorderDateText}
        </div>
      }

      {renderProtectionPlanModal &&
        <ProtectionPlanModal
          onToggle={setProtectionPlanModalOpen}
          isOpen={isProtectionPlanModalOpen}
          availableProtectionPlans={availableProtectionPlans}
          listingId={listing.id}
          disabled={isBulkItem}
          protectionPlan={displayedProtectionPlan}
          trackEventFn={(componentName, protectionPlan) => trackProtectionPlanEvent(componentName, MParticleEventName.ClickedAddedProtectionPlan, protectionPlan, listing)}
          onUpdate={onProtectionPlanModalSubmit}
          onClose={onProtectionPlanModalClose}
          additionalCoverageDetails={additionalCoverageDetails}
        />
      }
    </div>
  );
}

function useViewModel({
  quantity,
  listing,
  disabled,
  position,
  parentComponentName,
  location,
}: {
  listing: Listing,
  quantity: number,
  disabled: boolean,
  position: number,
  parentComponentName: string,
  location: Location,
}) {
  const user = useUser();

  const listingId = listing.id || '';
  const showPreorderText = !!listing.preorderInfo?.onPreorder;
  const title = showPreorderText ? t('preorder') : t('label');
  const showDisabled = !!disabled || isOwner(user, listing);
  const showDigitalListingLink = isFree(listing) && isDigitalListing(listing);
  const digitalListingUrlTarget = user?.deviceName === WEB ? '_blank' : '';

  function handleClick() {
    trackEvent({
      eventName: MParticleEventName.AddToCart,
      productActionType: ProductActionType.AddToCart,
      listing: listing,
      position: position,
      componentName: parentComponentName,
      currentView: resolveCurrentView(location),
    });

    trackCriteoAddToCart({
      id: listing.id,
      price: listing.pricing.buyerPrice.amount,
      quantity,
    }, user);

    trackFacebookEvent(
      ADD_TO_CART_EVENT,
      {
        content_type: PRODUCT_CONTENT_TYPE,
        content_ids: [listingId],
      },
    );
  }

  return {
    title,
    showDisabled,
    showPreorderText,
    showDigitalListingLink,
    preorderDateText: preorderDateText(listing.preorderInfo),
    freeListingAnchorTarget: digitalListingUrlTarget,
    handleClick,
    listingId,
  };
}

const DATE_FORMAT = 'LL';
function preorderDateText(preorder: AddToCartButtonFields.Fragment['preorderInfo']) {
  if (!preorder?.onPreorder) {
    return null;
  }

  const { seconds } = preorder.estimatedShipDate;

  return t('preorderEstimatedShipDate', {
    date: formatInternationalizedSeconds(seconds, DATE_FORMAT),
  });
}

function t(key: string, opt = {}): string {
  return I18n.t(`commons.addToCartButton.${key}`, opt);
}

function isFree(listing: Listing) {
  return listing?.pricing?.buyerPrice?.amountCents === 0;
}

function isDigitalListing(listing: Listing): boolean {
  return listing?.listingType === ListingType.DIGITAL;
}

export const AddToCartButtonFragment = gql`
  fragment AddToCartButtonFields on Listing {
    _id
    id
    sellerId
    listingType
    pricing {
      buyerPrice {
        amount
        amountCents
        currency
      }
    }
    preorderInfo {
      onPreorder
      estimatedShipDate {
        seconds
      }
    }
  }
`;

export default withRouter(AddToCartButton);
